'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { XIcon } from 'lucide-react';
import { useEvent } from 'react-use';

import { Thread } from '@/components/assistant-ui/thread';
import { ThreadListSidebar } from '@/components/assistant-ui/threadlist-sidebar';
import { NetworkStatusMonitor } from '@/components/network-status-monitor';
import { useConfig } from '@/components/providers/config-provider';
import { DebouncedNetworkStateProvider } from '@/components/providers/debounced-network-state-provider';
import { useReadOnlyLoading } from '@/components/providers/read-only-runtime-provider';
import { ToasterProvider } from '@/components/toaster-context';
import { ToasterWithSidebar } from '@/components/toaster-with-sidebar';
import { TtsPlaybackErrorMonitor } from '@/components/tts-playback-error-monitor';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { HeaderActions } from '@/components/ui/header-actions';
import { Separator } from '@/components/ui/separator';
import { SidebarInset, SidebarProvider, useSidebar } from '@/components/ui/sidebar';
import { SidebarTrigger } from '@/components/ui/sidebar-trigger';
import { Spinner } from '@/components/ui/spinner';
import { useAutoResumeOnReconnect } from '@/hooks/use-auto-resume';
import { useLocalizedConfig } from '@/hooks/use-localized-config';
import { useTrackEvent } from '@/hooks/use-track-event';
import { WIDGET_ANALYTICS_EVENTS, WIDGET_TRIGGERS } from '@/lib/constants/post-message-types';
import { onThreadSwitchStart } from '@/lib/thread-switch-signal';
import { cn } from '@/lib/utils';

type LayoutProps = {
  mode?: 'default' | 'embed';
  defaultSidebarOpen?: boolean;
  onSidebarOpenChange?: (open: boolean) => void;
  hideSidebar?: boolean;
};

function LayoutContent({ mode, hideSidebar }: LayoutProps) {
  const { state, forceOverlay, isMobile } = useSidebar();
  const { assetBasePath, branding } = useConfig();
  const sidebarTitle = useLocalizedConfig<string>('navigation.sidebarTitle');
  const trackEvent = useTrackEvent();
  const { breadcrumbIcon, title: sidebarTitleConfig } = branding?.components?.sidebar ?? {};
  const breadcrumbIconSrc =
    breadcrumbIcon?.path && assetBasePath ? `${assetBasePath}/${breadcrumbIcon.path}` : null;
  const isLoadingMessages = useReadOnlyLoading();
  const [settling, setSettling] = useState(false);
  const composerRef = useRef<HTMLDivElement>(null);
  const [composerHeight, setComposerHeight] = useState(0);
  const onReadyRef = useRef<(() => void) | null>(null);
  const overlayVisibleRef = useRef(false);
  const safetyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const overlayVisible = settling || isLoadingMessages;

  useEffect(() => {
    if (overlayVisible && composerRef.current) {
      setComposerHeight(composerRef.current.offsetHeight);
    }
  }, [overlayVisible]);

  useEffect(() => {
    overlayVisibleRef.current = overlayVisible;
  }, [overlayVisible]);

  useEffect(() => {
    return onThreadSwitchStart(onReady => {
      if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);

      if (overlayVisibleRef.current) {
        // Already at full opacity — no transition fires, so onTransitionEnd never
        // calls onReady; do it on the next frame instead.
        onReadyRef.current = null;
        requestAnimationFrame(() => onReady());
        return;
      }
      onReadyRef.current = onReady;
      setSettling(true);

      // Safety net if transitionend never fires.
      safetyTimerRef.current = setTimeout(() => {
        if (onReadyRef.current) {
          onReadyRef.current();
          onReadyRef.current = null;
        }
      }, 10_000);
    });
  }, []);

  const handleOverlayTransitionEnd = useCallback((e: React.TransitionEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (onReadyRef.current) {
      if (safetyTimerRef.current) {
        clearTimeout(safetyTimerRef.current);
        safetyTimerRef.current = null;
      }
      onReadyRef.current();
      onReadyRef.current = null;
    }
  }, []);

  useAutoResumeOnReconnect();

  const isEmbed = mode === 'embed';

  const closeWidget = useCallback(() => {
    if (!isEmbed) return;
    trackEvent({
      event: WIDGET_ANALYTICS_EVENTS.CLOSED,
      properties: { trigger: WIDGET_TRIGGERS.CLOSE_BUTTON },
    });
    window.parent.postMessage({ type: 'WIDGET_EVENT', action: 'closed' }, '*');
  }, [isEmbed, trackEvent]);

  useEvent('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Escape' && isEmbed) closeWidget();
  });

  return (
    <div className="bazak-branding relative flex h-full max-h-full w-full overflow-hidden pe-0.5">
      {!hideSidebar && <ThreadListSidebar forceOverlay={isEmbed} />}
      <SidebarInset className="flex h-full max-h-full flex-col overflow-hidden">
        <header
          data-header
          className="flex h-12 shrink-0 items-center gap-3 border-b border-border px-3"
        >
          {!hideSidebar && <SidebarTrigger />}
          <div
            className={cn(
              'flex items-center transition-opacity duration-200',
              state === 'collapsed' || isMobile || forceOverlay
                ? 'opacity-100'
                : 'opacity-100 @3xl:pointer-events-none @3xl:opacity-0',
            )}
          >
            <Separator orientation="vertical" className="me-2 h-4" />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbPage>
                    {breadcrumbIconSrc && (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        src={breadcrumbIconSrc}
                        alt=""
                        width={breadcrumbIcon?.width ?? 16}
                        className="me-1.5 h-auto"
                      />
                    )}
                    {!sidebarTitleConfig?.hidden && (
                      <span
                        style={{
                          ...(sidebarTitleConfig?.color
                            ? { color: sidebarTitleConfig.color }
                            : undefined),
                          ...(sidebarTitleConfig?.fontWeight
                            ? { fontWeight: sidebarTitleConfig.fontWeight }
                            : undefined),
                        }}
                      >
                        {sidebarTitle}
                      </span>
                    )}
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
          <div className="ms-auto">
            <HeaderActions />
          </div>
          {isEmbed && (
            <Button
              variant="ghost"
              size="icon"
              className="size-7 shrink-0 text-primary"
              onClick={closeWidget}
            >
              <XIcon className="size-4" />
            </Button>
          )}
        </header>
        <div className="relative flex min-h-0 flex-1">
          <Thread onSettlingChange={setSettling} composerRef={composerRef} />
          <div
            className={cn(
              // Between the thread's own two layers: over a sticky results header, which would
              // otherwise stand on the next thread's blank page, and under the composer, which
              // stays usable while that thread loads.
              'pointer-events-none absolute inset-0 z-[35] flex items-center justify-center bg-background transition-opacity duration-200 ease-out',
              settling || isLoadingMessages ? 'opacity-100' : 'opacity-0',
            )}
            style={composerHeight > 0 ? { paddingBottom: composerHeight } : undefined}
            onTransitionEnd={handleOverlayTransitionEnd}
          >
            {isLoadingMessages && <Spinner size="lg" />}
          </div>
        </div>
      </SidebarInset>
    </div>
  );
}

export function Layout({
  mode = 'default',
  defaultSidebarOpen,
  onSidebarOpenChange,
  hideSidebar,
}: LayoutProps) {
  const isEmbed = mode === 'embed';

  return (
    <DebouncedNetworkStateProvider>
      <ToasterProvider toasterId="assistant">
        <SidebarProvider
          defaultOpen={defaultSidebarOpen}
          onOpenChange={onSidebarOpenChange}
          forceOverlay={isEmbed}
        >
          <LayoutContent mode={mode} hideSidebar={hideSidebar} />
          <ToasterWithSidebar forceOverlay={isEmbed} />
          <NetworkStatusMonitor />
          <TtsPlaybackErrorMonitor />
        </SidebarProvider>
      </ToasterProvider>
    </DebouncedNetworkStateProvider>
  );
}
